import { z } from 'zod';

// runtime config injected by preview-ui's entrypoint (/env.js); empty or
// absent everywhere else, so behaviour is unchanged [STMN-3418]
declare global {
  interface Window {
    __ENV__?: Record<string, unknown>;
  }
}

const configSchema = z.object({
  VITE_API_URL: z.string().url(),
  VITE_ANALYTICS_URL: z.string().url(),
  VITE_MEDIA_API_URL: z.string().url(),
  VITE_ACCOUNT_API_URL: z.string(),
  VITE_ACCOUNT_URL: z.string(),
  VITE_TICKET_API_URL: z.string().url(),
  VITE_STUDIO_URL: z.string().url(),
  VITE_SENTRY_DEBUG: z.boolean(),
  VITE_CLIENT_ID: z.string(),
  VITE_ENVIRONMENT: z.enum([
    'local',
    'staging',
    'preprod',
    'preprod-us',
    'release',
    'release-us',
    'demo'
  ]),
  ROOT_API_URL: z.string(),
  VITE_DISABLE_ASK_BEFORE_LEAVING: z.boolean().optional(),
  FEATURE_FLAGS: z.object({
    USE_OLD_WF_OVERVIEW: z.boolean(),
    USE_BATCH_UPDATE: z.boolean(),
    USE_BATCH_ACTION: z.boolean(),
    USE_SANDBOX: z.boolean(),
    USE_ARCHIVAGE_FEATURE: z.boolean(),
    USE_WORKFLOW_CARDS: z.boolean(),
    USE_STATE_DIFF: z.boolean(),
    USE_BLOCKCHAIN_INFO: z.boolean()
  })
});

type Config = z.infer<typeof configSchema>;

const configs = {
  local: {
    VITE_API_URL: 'http://trace-api.local.stratumn.com:4100',
    VITE_ACCOUNT_API_URL: 'http://account-api.local.stratumn.com:4200',
    VITE_ACCOUNT_URL: 'http://account.local.stratumn.com:4201',
    VITE_ANALYTICS_URL: 'http://analytics.local.stratumn.com:4802',
    VITE_MEDIA_API_URL: 'http://media-api.local.stratumn.com:4500',
    VITE_TICKET_API_URL: 'http://ticket-api.local.stratumn.com:4700',
    VITE_STUDIO_URL: 'http://studio.local.stratumn.com:5001',
    VITE_CLIENT_ID: '1',
    ROOT_API_URL: 'local.stratumn.com',
    VITE_SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'local',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: true,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: true
    }
  },
  staging: {
    VITE_MEDIA_API_URL: 'https://media-api.staging.stratumn.com',
    VITE_API_URL: 'https://trace-api.staging.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.staging.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.staging.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.staging.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.staging.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.staging.stratumn.com',
    VITE_ENVIRONMENT: 'staging',
    ROOT_API_URL: 'staging.stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: true,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: true
    }
  },
  preprod: {
    VITE_MEDIA_API_URL: 'https://media-api.preprod.stratumn.com',
    VITE_API_URL: 'https://trace-api.preprod.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.preprod.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.preprod.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.preprod.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.preprod.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.preprod.stratumn.com',
    VITE_ENVIRONMENT: 'preprod',
    ROOT_API_URL: 'preprod.stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: false,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: true
    }
  },
  preprod_us: {
    VITE_MEDIA_API_URL: 'https://media-api.preprod.us.stratumn.com',
    VITE_API_URL: 'https://trace-api.preprod.us.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.preprod.us.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.preprod.us.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.preprod.us.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.preprod.us.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.preprod.us.stratumn.com',
    VITE_ENVIRONMENT: 'preprod-us',
    ROOT_API_URL: 'preprod.us.stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: false,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: false
    }
  },
  release: {
    VITE_MEDIA_API_URL: 'https://media-api.stratumn.com',
    VITE_API_URL: 'https://trace-api.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.stratumn.com',
    VITE_ENVIRONMENT: 'release',
    ROOT_API_URL: 'stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: false,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: true
    }
  },
  release_us: {
    VITE_MEDIA_API_URL: 'https://media-api.us.stratumn.com',
    VITE_API_URL: 'https://trace-api.us.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.us.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.us.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.us.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.us.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.us.stratumn.com',
    VITE_ENVIRONMENT: 'release-us',
    ROOT_API_URL: 'us.stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: true,
      USE_SANDBOX: false,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: false
    }
  },
  demo: {
    VITE_MEDIA_API_URL: 'https://media-api.demo.stratumn.com',
    VITE_API_URL: 'https://trace-api.demo.stratumn.com',
    VITE_ACCOUNT_API_URL: 'https://account-api.demo.stratumn.com',
    VITE_TICKET_API_URL: 'https://ticket-api.demo.stratumn.com',
    VITE_CLIENT_ID: '1',
    VITE_SENTRY_DEBUG: false,
    VITE_ACCOUNT_URL: 'https://account.demo.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.demo.stratumn.com',
    VITE_STUDIO_URL: 'https://studio.demo.stratumn.com',
    VITE_ENVIRONMENT: 'demo',
    ROOT_API_URL: 'demo.stratumn.com',
    VITE_DISABLE_ASK_BEFORE_LEAVING: false,
    FEATURE_FLAGS: {
      USE_OLD_WF_OVERVIEW: false,
      USE_BATCH_UPDATE: true,
      USE_BATCH_ACTION: false,
      USE_SANDBOX: false,
      USE_ARCHIVAGE_FEATURE: true,
      USE_WORKFLOW_CARDS: true,
      USE_STATE_DIFF: true,
      USE_BLOCKCHAIN_INFO: true
    }
  }
} satisfies Record<string, Config>;

type EnvName = keyof typeof configs;

const getConfig = () => {
  const hostname = window.location.hostname;
  let envConfig: Config;

  if (hostname.includes('staging')) {
    envConfig = configs.staging;
  } else if (hostname.includes('preprod.us')) {
    envConfig = configs.preprod_us;
  } else if (hostname.includes('preprod')) {
    envConfig = configs.preprod;
  } else if (hostname.includes('.us.')) {
    envConfig = configs.release_us;
  } else if (hostname.includes('local')) {
    envConfig = configs.local;
  } else if (hostname.includes('demo')) {
    envConfig = configs.demo;
  } else {
    envConfig = configs.release;
  }

  const mode = import.meta.env.VITE_MODE as EnvName | undefined;
  if (mode && mode in configs) {
    envConfig = configs[mode];
    envConfig.FEATURE_FLAGS.USE_SANDBOX = true;
  }

  const mergedConfig = Object.keys(envConfig).reduce<Record<string, unknown>>(
    (acc, key) => {
      const envValue = import.meta.env[key as keyof ImportMeta];
      acc[key] = envValue !== undefined ? envValue : envConfig[key];
      return acc;
    },
    {}
  );

  // Runtime override injected via /env.js (review apps): wins over build-time
  const runtimeConfig = {
    ...mergedConfig,
    ...(window.__ENV__ ?? {})
  };

  const validatedConfig = configSchema.safeParse(runtimeConfig);

  if (!validatedConfig.success) {
    console.error(validatedConfig.error);
    throw new Error('Configuration validation failed');
  }

  return validatedConfig.data;
};

export const env = getConfig();
